import React from "react";

import { ApplicationRightService, IApplicationRightService } from 'services/ApplicationRightService';
import { ModuleService } from 'services/ModuleService';
import { isBetaUser } from "components/helpers/AuthHelpers";
import uuid from "react-uuid";
import { NavItemTypes } from "services/SystemNames";
import { DrawerMenuItemProps } from "./props/DrawerMenuItemProps";

// Modules
import { ApplicationRightProps, ModuleProps } from "components/props";

export interface IDrawerMenuService {
    getMenuItems(applicationRights: ApplicationRightProps[]): DrawerMenuItemProps[];
}

export class DrawerMenuService {

    _applicationRightService:IApplicationRightService;
    _menuItems: DrawerMenuItemProps[] = [];

    constructor(
        applicationRights: ApplicationRightProps[]
    ) {
        this._applicationRightService = new ApplicationRightService(applicationRights);
    }

    getMenuItems(): DrawerMenuItemProps[] {
        this._menuItems = [];

        const homeMenuItem = this.createMenuItem({
            to: "/", icon: "house", title: "Hjem"
        });
        this._menuItems.push(homeMenuItem);

        if(navigator.onLine) {
            this.createOnlineMenuItems();
        } else {
            this.createOfflineMenuItems();
        }

        this._menuItems.push(this.createAboutMenuItem());
        this._menuItems.push(this.createLogoutMenuItem());

        return this._menuItems;
    }

    createOnlineMenuItems() {
        for (const parentModule of ModuleService.getParentModules()) {
            this.createAndPushMenuItem(parentModule);
        }
    }

    createOfflineMenuItems() {
        for (const parentModule of ModuleService.getOfflineParentModules()) {
            this.createAndPushMenuItem(parentModule);
        }
    }

    createAndPushMenuItem(module: ModuleProps) {
        const menuItem = this.createAndPushMenuItemRecursive(module, true); // true = is parent level
        if (menuItem) {
            this._menuItems.push(menuItem);
        }
    }

    createAndPushMenuItemRecursive(module: ModuleProps, isParentLevel = false): DrawerMenuItemProps | null {
        // Check access rights first
        if (!this._applicationRightService.canAccess(module.applicationRights || [])) {
            return null;
        }

        if (!module.hasMenuItem) return null;

        // Create the menu item - only include icon at parent level
        const menuItem = this.createMenuItem({
            to: module.url,
            icon: isParentLevel ? module.icon : undefined, // Only add icon at parent level
            title: module.title,
        });

        switch (module.id) {
            case "SETTINGS_RESET_DATA":
                menuItem.type = NavItemTypes.RefreshData;
                break;
            case "SETTINGS_IMPERSONATE":
                menuItem.type = NavItemTypes.Impersonate;
                break;
            default: break;
        }

        // Recursively process children (not parent level)
        const childModules = ModuleService.getChildModules(module.id);
        for (const childModule of childModules) {
            const childMenuItem = this.createAndPushMenuItemRecursive(childModule, false); // false = not parent level
            if (childMenuItem) {
                menuItem.children.push(childMenuItem);
            }
        }

        return menuItem;
    }

    createMenuItem(properties:any):DrawerMenuItemProps {
        const menuItem:DrawerMenuItemProps = {
            id: uuid(),
            to: ('to' in properties) ? properties.to : "",
            icon: ('icon' in properties) ? properties.icon : "",
            title: ('title' in properties) ? properties.title : "",
            type: ('type' in properties) ? properties.type : "",
            children: ('children' in properties) ? properties.children : [],
            beta: ('beta' in properties) ? properties.beta : false,
            menutype: "item",
            expanded: false
        }

        return menuItem;
    }

    pushMenuItem(module:ModuleProps, menuItem:DrawerMenuItemProps) {
        if(!this._applicationRightService.canAccess(module.applicationRights)) return;
        this._menuItems.push(menuItem);
    }

    createAboutMenuItem() {
        return this.createMenuItem({
            icon: "info", title: "Om Njord Pilot Web", type: NavItemTypes.About
        });
    }

    createLogoutMenuItem() {
        return this.createMenuItem({
            icon: "sign-out", title: "Logg ut", type: NavItemTypes.Logout
        });
    }
}
